/**
 * This is the front-end version of the back-end's benchling/app_core/framework/utils/ which similarly has
 * utilities for type introspection and API ID validation.

 * If you have any questions about this file or anything in the framework, come talk to App Core or schedule
 * time to chat with us at Core Object Office Hours!
 */

import {objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';

import {
  DOMAIN_GRAPH_TYPE_TO_MODEL_TYPE_CONSTANT,
  MODEL_TYPE_CONSTANT_TO_DOMAIN_GRAPH_TYPE,
} from './CoreObjectCompatibilityMappings';
import {
  CoreObject,
  CoreObjectId,
  CoreObjectIdPrefix,
  DomainGraphInterface,
  DomainGraphType,
  InterfaceType,
  MaybeCoreObject,
  ObjectType,
} from './CoreObjectConstants';
import {
  DOMAIN_GRAPH_INTERFACE_INTEGRATION_MAP,
  DOMAIN_GRAPH_TYPE_TO_DEFAULT_API_BASE,
} from './GeneratedDomainGraphMappings';
import {
  ID_PREFIX_TO_DOMAIN_GRAPH_TYPES,
  ID_PREFIX_TO_DOMAIN_GRAPH_TYPES_ENTRIES,
} from './GeneratedDomainGraphType';

/**
 * TODO: These resolutions are not correct. Do not add to this list without talking to Domain Graph first.
 * @package
 */
export const PREFIX_DISAMBIGUATION_FUNCTIONS: Partial<
  Record<CoreObjectIdPrefix, (object: CoreObject) => ObjectType>
> = {
  assaysch: (object: CoreObject) => DomainGraphType.RUN_SCHEMA,
  // TODO: Frontend cannot distinguish app installations from legacy app installations by ID alone.
  // Defaults to AppInstallation (catalog-backed); legacy installations require backend disambiguation.
  app: (object: CoreObject) => DomainGraphType.APP_INSTALLATION,
  // Frontend cannot distinguish users, organizations, and service principals (shared `ent` prefix) by ID
  // alone. Defaults to User; the others require backend disambiguation.
  ent: (object: CoreObject) => DomainGraphType.USER,
  // TODO(BNCH-97486): Switch to interface-based checks for plates and plate schemas.
  plt: (object: CoreObject) => DomainGraphType.PLATE,
  pltsch: (object: CoreObject) => DomainGraphType.PLATE_SCHEMA,
  seq: (object: CoreObject) => DomainGraphType.DNA_SEQUENCE,
  src: (object: CoreObject) => DomainGraphType.PROJECT,
  // Currently, we are using ModelTypeConstants.BASIC_FOLDER_ITEM for both custom entities and
  // SSIs that are backed by custom entities (i.e. complex polymers) so we resolve this to
  // the custom entity domain graph type for all bfi-prefixed entities.
  // Ideally, this will be improved for exclusive SSIs.
  bfi: (object: CoreObject) => DomainGraphType.CUSTOM_ENTITY,
  jfsch: (object: CoreObject) => DomainGraphType.JELLYFISH_SCHEMA,
  wffccv: (object: CoreObject) => DomainGraphType.WORKFLOW_FLOWCHART_CONFIG_VERSION,
  ts: (object: CoreObject) => DomainGraphType.ENTITY_SCHEMA,
  tsf: (object: CoreObject) => DomainGraphType.BASE_FIELD_DEFINITION,
};

// Vendored in from APIUtil, need to do some merging
function getObjectTypePrefix(id: string): CoreObjectIdPrefix | null {
  const index = id.indexOf('_');
  if (index !== -1) {
    const prefix = id.slice(0, index) as CoreObjectIdPrefix;
    return ID_PREFIX_TO_DOMAIN_GRAPH_TYPES.hasOwnProperty(prefix) ? prefix : null;
  } else {
    return null;
  }
}

/**
 * Identity introsepection
 */

export function isCoreObjectId(id: string): id is CoreObjectId {
  return getObjectTypePrefix(id) != null;
}

export function asCoreObjectId(id: string): CoreObjectId {
  assert(isCoreObjectId(id), `Expected ${id} to be a valid core object id`);
  return id;
}

export function isCoreObject(item?: MaybeCoreObject): item is CoreObject {
  return item?.id != null ? isCoreObjectId(item.id) : false;
}

export function asCoreObject(item?: MaybeCoreObject): CoreObject {
  assert(isCoreObject(item), `Expected ${item} to be a valid core object`);
  return item;
}

export function getCoreObjectIds(ids: Array<string>): Array<CoreObjectId> {
  return ids.filter((id) => isCoreObjectId(id));
}

export function getCoreObjects(items: Array<MaybeCoreObject>): Array<CoreObject> {
  return items.filter((item) => isCoreObject(item));
}

export function getObjectType(object: CoreObject): ObjectType {
  const prefix = getObjectTypePrefix(object.id);
  assert(prefix != null, `Invalid prefix for object with id ${object.id}`);
  const types = Array.from(ID_PREFIX_TO_DOMAIN_GRAPH_TYPES[prefix]);
  if (types.length === 1) {
    return types[0];
  } else if (PREFIX_DISAMBIGUATION_FUNCTIONS[prefix] != null && object != null) {
    return PREFIX_DISAMBIGUATION_FUNCTIONS[prefix](object);
  } else {
    throw Error(
      'Encountered prefix representing ambiguous types without disambiguation function defined - ' +
        `${prefix}: ${types}`
    );
  }
}

export function getCoreObjectsOfType(
  items: Array<MaybeCoreObject>,
  objectType: ObjectType
): Array<CoreObject> {
  return items.filter(
    (item) => isCoreObject(item) && getObjectType(item) === objectType
  ) as Array<CoreObject>;
}

/**
 * Returns the ModelTypeConstants for the given ObjectType.
 *
 * Some ObjectTypes (listed in DG_TYPES_WITHOUT_MODEL_TYPE_CONSTANT) have no corresponding
 * ModelTypeConstants because they are backend-only or have no frontend representation.
 * This function asserts that the ObjectType has a mapping.
 *
 * @throws {Error} if the ObjectType has no corresponding ModelTypeConstants
 */
export function getModelTypeConstantForObjectType(objectType: ObjectType): ModelTypeConstants {
  const modelType = DOMAIN_GRAPH_TYPE_TO_MODEL_TYPE_CONSTANT[objectType];
  assert(modelType != null, `No ModelTypeConstants mapping for ObjectType: ${objectType}`);
  return modelType;
}

export function getObjectTypeForModelTypeConstant(modelType: ModelTypeConstants): ObjectType | undefined {
  return MODEL_TYPE_CONSTANT_TO_DOMAIN_GRAPH_TYPE[modelType];
}

/**
 * Object <-> Capability integration introspection
 */

export function isCoreObjectWithInterface(
  object: MaybeCoreObject,
  interfaceType: InterfaceType
): object is CoreObject {
  return isCoreObject(object) && doesObjectHaveInterface(object, interfaceType);
}

export function getCoreObjectsWithInterface(
  items: Array<MaybeCoreObject>,
  interfaceType: InterfaceType
): Array<CoreObject> {
  return getCoreObjects(items).filter((object) => doesObjectHaveInterface(object, interfaceType));
}

export function getObjectTypesWithInterface(interfaceType: InterfaceType): Set<ObjectType> {
  return DOMAIN_GRAPH_INTERFACE_INTEGRATION_MAP[interfaceType];
}

export function doesObjectTypeHaveInterface(type: ObjectType, interfaceType: InterfaceType): boolean {
  return getObjectTypesWithInterface(interfaceType).has(type);
}

export function getInterfacesForObjectType(objectType: ObjectType): Array<InterfaceType> {
  const capabilities: Array<InterfaceType> = [];
  for (const capability of Object.keys(DomainGraphInterface)) {
    if (DOMAIN_GRAPH_INTERFACE_INTEGRATION_MAP[capability as InterfaceType].has(objectType)) {
      capabilities.push(capability as InterfaceType);
    }
  }
  return capabilities;
}

export function getCapabilitiesForModelType(modelType: ModelTypeConstants): Array<InterfaceType> {
  const coreObjectType = getObjectTypeForModelTypeConstant(modelType);
  if (!coreObjectType) {
    return [];
  }
  return getInterfacesForObjectType(coreObjectType);
}

export function doesObjectHaveInterface(object: CoreObject, interfaceType: InterfaceType): boolean {
  return doesObjectTypeHaveInterface(getObjectType(object), interfaceType);
}

/**
 * Object configuration introspection
 */

const DOMAIN_GRAPH_TYPE_TO_API_BASE: Record<ObjectType, string> = {
  ...DOMAIN_GRAPH_TYPE_TO_DEFAULT_API_BASE,
  [DomainGraphType.BOX]: '/1/api/grids', // TODO: introduce '/boxes' -> proxy to existing '/grids' behavior
  [DomainGraphType.CUSTOM_ENTITY]: '/1/api/basic-folder-items',
  [DomainGraphType.CONFIG_IMPORT_HISTORY_ENTRY]: '1/api/config-import-history-entry',
  [DomainGraphType.DNA_OLIGO]: '/1/api/sequences',
  [DomainGraphType.DNA_SEQUENCE]: '/1/api/sequences',
  [DomainGraphType.ENTITY_SCHEMA]: '/1/api/tag-schemas',
  [DomainGraphType.MATRIX_PLATE_SCHEMA]: '/1/api/plate-schemas',
  [DomainGraphType.PLATE]: '/1/api/grids', // TODO: introduce '/plates' -> proxy to existing '/grids' behavior
  [DomainGraphType.PROJECT]: '/1/api/sources',
  [DomainGraphType.REGISTRY]: '/1/api/sources',
  [DomainGraphType.REQUEST_SCHEMA]: '/1/api/assay-schemas',
  [DomainGraphType.RESULT]: '/1/api/assay-results',
  [DomainGraphType.RESULT_SCHEMA]: '/1/api/assay-schemas',
  [DomainGraphType.RNA_OLIGO]: '/1/api/sequences',
  [DomainGraphType.RNA_SEQUENCE]: '/1/api/sequences',
  [DomainGraphType.RUN_SCHEMA]: '/1/api/assay-schemas',
  [DomainGraphType.FIELDSET]: '/1/api/schema-interfaces',
  [DomainGraphType.WAREHOUSE_CREDENTIAL]: '/1/api/warehouse/logins',
  [DomainGraphType.WAREHOUSE_CUSTOM_VIEW]: '/1/api/warehouse/user-defined-views',
  [DomainGraphType.WELL_PLATE]: '/1/api/grids', // TODO: introduce '/plates' -> proxy to existing '/grids' behavior
  [DomainGraphType.WELL_PLATE_SCHEMA]: '/1/api/plate-schemas',
  [DomainGraphType.WORKFLOW_FLOWCHART_CONFIG_VERSION]: '/1/api/workflow-flowchart-config-versions',
};

/**
 * Returns the legacy `/1/api` base for an object type (used to build both read and write endpoints).
 *
 * Deprecated for new shared/platform-wide *write* callers: such components must not infer an object's
 * write API route from its type. Prefer a registered `type -> async write function` so shared flows
 * stay agnostic to the underlying path (legacy `/1/api` today, internal V3 REST tomorrow). Reads and
 * feature-local single-type callers may still use this. See `docs/frontend/api-route-write-dispatch.md`.
 */
export function getApiBaseForObjectType(objectType: ObjectType): string {
  return DOMAIN_GRAPH_TYPE_TO_API_BASE[objectType];
}

const DOMAIN_GRAPH_TYPE_TO_ID_PREFIX: Record<ObjectType, CoreObjectIdPrefix> = objectFromEntries(
  ID_PREFIX_TO_DOMAIN_GRAPH_TYPES_ENTRIES.flatMap(([prefix, types]) =>
    [...types].map((type) => [type, prefix] as [ObjectType, CoreObjectIdPrefix])
  )
);

export function getIdPrefixForObjectType(objectType: ObjectType): CoreObjectIdPrefix {
  return DOMAIN_GRAPH_TYPE_TO_ID_PREFIX[objectType];
}
